import React, { createContext, useContext, useMemo } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';
import { useSession } from '@clerk/clerk-react';
import { createSupabaseClientWithSession } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type SupabaseClientContextType = {
  supabase: SupabaseClient<Database>;
};

const SupabaseClientContext = createContext<SupabaseClientContextType | undefined>(undefined);

export function SupabaseClientProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();

  const supabase = useMemo(() => {
    console.log('[SupabaseClientProvider] Creating Supabase client with Clerk session:', !!session);

    return createSupabaseClientWithSession(
      session
        ? async () => {
            try {
              // Get the default JWT token from Clerk
              // The template system has been deprecated, just use the default token
              const token = await session.getToken();
              if (!token) {
                console.warn('[SupabaseClientProvider] Clerk returned empty token');
              }
              return token ?? null;
            } catch (error) {
              console.warn('[SupabaseClientProvider] Failed to obtain Clerk session token for Supabase', error);
              return null;
            }
          }
        : null
    );
  }, [session]);

  return (
    <SupabaseClientContext.Provider value={{ supabase }}>
      {children}
    </SupabaseClientContext.Provider>
  );
}

/**
 * Hook to access the Clerk-authenticated Supabase client.
 * This client automatically includes the Clerk session token in all requests,
 * enabling RLS policies to verify user identity via auth.jwt() and auth.uid().
 */
export function useSupabase() {
  const context = useContext(SupabaseClientContext);
  if (!context) {
    throw new Error('useSupabase must be used within SupabaseClientProvider');
  }
  return context.supabase;
}
